import type { TextAreaRef } from "antd/lib/input/TextArea";
import {
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import {
    ASK_COMPOSER_LISTENING_PLACEHOLDER,
    ASK_COMPOSER_TEXTAREA_CLASS,
    AskComposerField,
    AskComposerMic,
    useAskComposerDictation,
} from "searchAsk/AskComposerField";
import { type IAskPredictedQuery, NO_SUGGESTIONS } from "searchAsk/askSuggest";
import { getUserKey, useAskSuggest } from "searchAsk/useAskSuggest";

import { IconArrowLeft, IconSparkle } from "@btnet/blueprint-icons";
import { BTButton } from "@btnet/core-components/BTButton";
import { BTText } from "@btnet/core-components/BTText";
import { BTTextArea } from "@btnet/core-components/BTTextArea";
import { FeedbackTarget } from "@btnet/services-btnet/handlers/models/feedbackTarget";
import { cn } from "@btnet/utilities-core";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";

import FeedbackWidget from "commonComponents/feedback/FeedbackWidget/FeedbackWidget";
import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";
import { useEntityNavigation } from "commonComponents/utilities/MainNavigation/SearchDialog/entity/useEntityNavigation";
import type { ISearchHit } from "commonComponents/utilities/MainNavigation/SearchDialog/search/search.types";
import { useAuthToken } from "commonComponents/utilities/MainNavigation/SearchDialog/search/useAuthToken";
import { useSearch } from "commonComponents/utilities/MainNavigation/SearchDialog/search/useSearch";
import { useSearchFeedbackExtraInfo } from "commonComponents/utilities/MainNavigation/SearchDialog/search/useSearchFeedbackExtraInfo";

import "./SearchDialog.less";

import { useWarmSearchAgent } from "./agentAnswer/useSearchAgent";
import { BrowseView, useBrowseSections } from "./browse/BrowseView";
import { addRecentItem, getRecentItems, getRecentRefs } from "./browse/recentItems";
import { getTryAskingPrompts, tryAskingRefersToOpenRecord } from "./browse/tryAsking";
import {
    describeResults,
    isRetryableSearchError,
    normalizeQuery,
    QUERY_DEBOUNCE_MS,
    resolveResultsDisplay,
    SEARCH_COMMIT_TIMEOUT_MS,
} from "./results/resultsDisplay";
import { ResultsView, useResultSections } from "./results/ResultsView";
import { useSearchSuggest } from "./results/useSearchSuggest";
import {
    type ISearchDialogSection,
    SEARCH_DIALOG_LISTBOX_ID,
    SEARCH_DIALOG_OPTION_ID_PREFIX,
} from "./rows/SearchDialogList";
import type { ISearchDialogRow } from "./rows/searchDialogRow.types";
import {
    type AskArmedBy,
    type AssistKind,
    type DialogOutcome,
    type DialogTrigger,
    type GuessSource,
    type IDroppedShape,
    type NavMethod,
    type SearchPhase,
    summarizeRoutability,
    useSearchDialogLearn,
} from "./searchDialog.learn";
import {
    resolveAskArmedBy,
    resolveEnterAction,
    resolveView,
    verdictFor,
} from "./searchDialog.types";
import { SearchDialogFooter } from "./SearchDialogFooter";
import { describeOffered, type IAssistOffer, useAssistLearn } from "./useAssistLearn";
import { useForceSearchReplay } from "./useForceSearchReplay";

const FOCUSABLE =
    "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

/** No row highlighted. */
const NO_ROW = -1;

/** Guess a predicted word inline only when it clearly leads: at least MIN_GUESS_RECORDS records and MIN_GUESS_SHARE of the candidates. */
const MIN_GUESS_RECORDS = 3;
const MIN_GUESS_SHARE = 0.5;

const flattenRows = (sections: ISearchDialogSection[]) =>
    sections.flatMap((section) => section.rows);

/** What the `Search` phase events count, for the list the user was shown. */
interface ISearchCounts {
    resultCount: number;
    renderedCount: number;
    droppedUnroutable: number;
    droppedShapes: IDroppedShape[];
}

const NO_COUNTS: ISearchCounts = {
    resultCount: 0,
    renderedCount: 0,
    droppedUnroutable: 0,
    droppedShapes: [],
};

/**
 * One search's way through the phase events, so each is sent once and the way
 * the user left it can still be reported after its state is gone. Held in a
 * ref and mutated in place; nothing renders from it.
 */
interface ITrackedSearch {
    searchId: string;
    traceId: string | null;
    query: string;
    startedAt: number | null;
    /** The committed list's counts, once it has landed. */
    counts: ISearchCounts | null;
    /** When the committed list landed, which is when its rows render. */
    rowsAt: number | null;
    completeSent: boolean;
    failed: boolean;
    /** A row from its list was opened, or its query was handed to Ask. */
    used: boolean;
    /** Closed or cleared: nothing more is reported for it. */
    left: boolean;
    /** The typing assist this search came from, if one did. */
    ledBy: { ledByAssist: AssistKind; ledByAssistId: string } | null;
}

/** What goes to Ask besides the question. */
export interface IAskHandoff {
    /** The question is about the record the user has open. */
    aboutOpenRecord: boolean;
}

export interface ISearchDialogProps {
    onClose: () => void;
    /** Scopes recents so a shared machine does not mix two users' history. */
    userId: number;
    /** Passed in because `commonComponents` may not read builder flags directly. */
    builderInfo: BuilderInfo | null | undefined;
    /** Picks context-relevant browse prompts. */
    openRecordEntity: string | null;
    /** How the dialog was opened. Telemetry only — see searchDialog.learn. */
    trigger: DialogTrigger;
    /** Caller-supplied: the route is app knowledge, not commonComponents'. */
    onAskFullPage: (question: string, handoff?: IAskHandoff) => void;
}

/**
 * One centered modal hosting browse and results behind a single input.
 *
 * Search-only: a question is handed to the full-page Ask rather than answered
 * here, so Ask is a doorway and not a third view. Being a modal rather than a
 * nav dropdown is what gives the result list its width, and what obliges the
 * focus trap and scroll lock.
 */
export function SearchDialog(props: ISearchDialogProps) {
    const { onClose, userId, builderInfo, openRecordEntity, trigger, onAskFullPage } = props;

    const [query, setQuery] = useState("");
    /** The key of a row the user moved to. Null, or a row no longer listed, falls back to the default highlight. */
    const [chosenKey, setChosenKey] = useState<string | null>(null);
    /** BTTextArea's handle; `inputRef` is the textarea inside it, which the caret code needs. */
    const fieldRef = useRef<TextAreaRef>(null);
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    // Typing and deleting both reach onChange; only onInput says which.
    const lastInputTypeRef = useRef<string | null>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const { doSearch, cancel, state } = useSearch({
        commitTimeoutMs: SEARCH_COMMIT_TIMEOUT_MS,
        getRecentRefs: () => getRecentRefs(userId),
    });

    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const closeFeedback = useCallback(() => {
        setIsFeedbackOpen(false);
        inputRef.current?.focus();
    }, []);
    const feedbackExtraInfo = useSearchFeedbackExtraInfo({
        surface: "dialog",
        searchId: state.searchId,
        traceId: state.traceId,
        query: state.searchQuery,
        results: state.committedResults,
    });

    const searchConfig = useContext(AppDefaultInfoContext)?.searchServiceConfiguration;
    const { getToken } = useAuthToken(searchConfig?.authAudience ?? "");
    // Opening the dialog is the earliest sign a question may be coming.
    useWarmSearchAgent();
    useForceSearchReplay();

    const learn = useSearchDialogLearn({ surface: "dialog" });

    const userKey = getUserKey(builderInfo?.builderId, userId);

    // Shared with the Ask page.
    const suggest = useAskSuggest({
        text: query,
        subject: null,
        enabled: true,
        userKey,
    });
    // A list for earlier text stands while the next loads only if the input
    // still starts with that text: never the empty input's under a name.
    const suggestCurrent =
        suggest.forText.trim().length === 0
            ? query.trim().length === 0
            : query.toLowerCase().startsWith(suggest.forText.toLowerCase());
    const suggestions = suggestCurrent ? suggest.suggestions : NO_SUGGESTIONS;

    // Shown while the input still starts with the text it was fetched for.
    const typeahead = useSearchSuggest(searchConfig?.apiBaseUrl ?? "", query, getToken, userKey);
    const typedKey = query.trim().toLowerCase();
    // Only for the text in the box: a correction of earlier text would replace
    // what has been typed since.
    const corrected =
        typedKey.length > 0 && typeahead.forQuery.toLowerCase() === typedKey
            ? typeahead.corrected
            : null;
    const names = useMemo(
        () =>
            typedKey.length > 0 &&
            typeahead.forQuery.length > 0 &&
            typedKey.startsWith(typeahead.forQuery.toLowerCase())
                ? typeahead.suggestions
                : [],
        [typedKey, typeahead.forQuery, typeahead.suggestions],
    );

    /**
     * Inline completion; the guessed suffix stays selected so typing overwrites it.
     * Priority: server whole-name completion, then a clearly-leading predicted word (MIN_GUESS_*), then the first listed name.
     */
    const prediction = useMemo((): { suffix: string; from: GuessSource } => {
        const none = { suffix: "", from: "prediction" as const };
        if (query.trim().length < 2 || query.endsWith(" ")) {
            return none;
        }
        const lower = query.toLowerCase();
        const extends_ = (text: string) =>
            text.length > query.length && text.toLowerCase().startsWith(lower);
        const pick = (text: string | undefined, from: GuessSource) =>
            text === undefined ? null : { suffix: text.slice(query.length), from };
        const whole = suggestions.completion?.text;
        // The name words the text could be finishing (one word, no more),
        // with how many records carry each: the guess is the likeliest one.
        const words = suggestions.queries.filter(
            (q) =>
                q.source === "name" &&
                q.count !== undefined &&
                extends_(q.text) &&
                !q.text.slice(query.length).includes(" "),
        );
        const wordTotal = words.reduce((sum, q) => sum + (q.count ?? 0), 0);
        const likely = (q: IAskPredictedQuery) =>
            q.source === "vocab" ||
            ((q.count ?? 0) >= MIN_GUESS_RECORDS &&
                wordTotal > 0 &&
                (q.count ?? 0) / wordTotal >= MIN_GUESS_SHARE);
        return (
            pick(whole !== undefined && extends_(whole) ? whole : undefined, "completion") ??
            pick(
                suggestions.queries.find((q) => extends_(q.text) && likely(q))?.text,
                "prediction",
            ) ??
            pick(names.find((s) => extends_(s.text))?.text, "name") ??
            none
        );
    }, [query, suggestions.completion, suggestions.queries, names]);
    // Offered only after a character is typed, never after a deletion.
    const [guessAllowed, setGuessAllowed] = useState(false);
    const inlineSuffix = guessAllowed ? prediction.suffix : "";
    useLayoutEffect(() => {
        const input = inputRef.current;
        if (input && inlineSuffix.length > 0 && document.activeElement === input) {
            input.setSelectionRange(query.length, query.length + inlineSuffix.length, "backward");
        }
    }, [query, inlineSuffix]);
    const dropGuess = useCallback(() => setGuessAllowed(false), []);

    // ── Typing-assist telemetry: each assist's offer as on screen, logged
    // shown → ended (see useAssistLearn). `assistIds` is read by the handlers
    // below, which run after these offers are known. ──
    const assistIds = { searchId: state.searchId || null, traceId: state.traceId };
    const guessOffer = useMemo((): IAssistOffer | null => {
        if (inlineSuffix.length === 0) {
            return null;
        }
        const offered = query + inlineSuffix;
        return {
            key: `guess:${offered.toLowerCase()}`,
            source: prediction.from,
            typed: query,
            count: 1,
            offered: [offered],
            ...describeOffered(query, offered),
        };
    }, [query, inlineSuffix, prediction.from]);
    const guessLearn = useAssistLearn(learn, "inline_completion", guessOffer, query, assistIds);

    /**
     * Bookkeeping for the two dialog-lifetime events.
     *
     * Refs rather than state throughout: none of it renders, and the close
     * event fires from an unmount cleanup that must not re-run when a counter
     * changes. `outcomeRef` is overwritten by whichever handler actually ends
     * the session, so the default of "abandoned" is what survives when nothing
     * did — which is the case worth counting.
     */
    const openedAtRef = useRef(Date.now());
    // Searches that finished, not ones started: the service already counts
    // those, and a pause in typing can start one that never finishes.
    const searchCountRef = useRef(0);
    const askCountRef = useRef(0);
    const sawResultsRef = useRef(false);
    const outcomeRef = useRef<DialogOutcome>("abandoned");
    /** Set when an assist is taken; the next search started claims it, and typing drops it. */
    const pendingLedByRef = useRef<AssistKind | null>(null);
    const learnRef = useRef(learn);
    useEffect(() => {
        learnRef.current = learn;
    }, [learn]);

    const hasQuery = query.trim().length > 0;
    const liveKey = normalizeQuery(query);

    // Resolved once so Ask arming and the live region agree. Rows are the
    // committed ranking narrowed by `isRoutable`.
    const { isRoutable } = useEntityNavigation(builderInfo);
    const display = useMemo(
        () =>
            resolveResultsDisplay(
                {
                    liveKey,
                    searchQuery: state.searchQuery,
                    phase: state.phase,
                    errorKind: state.errorKind,
                    committed: state.committedResults,
                    previous: state.previousCommitted,
                },
                isRoutable,
            ),
        [
            liveKey,
            state.searchQuery,
            state.phase,
            state.errorKind,
            state.committedResults,
            state.previousCommitted,
            isRoutable,
        ],
    );

    const searchIsForQuery = hasQuery && state.searchQuery === liveKey;
    const searchSettled =
        searchIsForQuery &&
        (state.phase === "done" || state.phase === "empty" || state.phase === "error");

    // The Ask row is the service's call alone: a query it sends to the agent.
    const askArmedBy = resolveAskArmedBy({
        query,
        searchType: verdictFor(state.searchType, liveKey, searchSettled),
    });
    const askMode = askArmedBy !== null;

    /**
     * Once per appearance, not per render: the row stays up across every
     * keystroke of a question, and that is one decision, not twenty.
     */
    const armedByRef = useRef<AskArmedBy | null>(null);
    useEffect(() => {
        const wasArmed = armedByRef.current !== null;
        armedByRef.current = askArmedBy;
        if (askArmedBy === null || wasArmed) {
            return;
        }
        // Only the search for what is typed: a verdict carried over from an
        // earlier form of the query belongs to that query's search.
        const ownSearch = hasQuery && state.searchQuery === liveKey;
        learnRef.current.learnAskArmed(
            ownSearch
                ? { searchId: state.searchId || null, traceId: state.traceId }
                : { searchId: null, traceId: null },
            { armedBy: askArmedBy },
        );
    }, [askArmedBy, hasQuery, liveKey, state.searchQuery, state.searchId, state.traceId]);

    const view = useMemo(() => resolveView({ query }), [query]);

    // The did-you-mean, logged where ResultsView renders it: in the results
    // view, in every state but an error.
    const correctionShown =
        corrected !== null && view.kind === "results" && display.kind !== "error";
    const correctionOffer = useMemo(
        (): IAssistOffer | null =>
            correctionShown && corrected !== null
                ? {
                      key: `dym:${typedKey}:${corrected.toLowerCase()}`,
                      source: "service",
                      typed: query,
                      count: 1,
                      offered: [corrected],
                      ...describeOffered(query, corrected),
                  }
                : null,
        [correctionShown, corrected, typedKey, query],
    );
    const correctionLearn = useAssistLearn(
        learn,
        "did_you_mean",
        correctionOffer,
        query,
        assistIds,
    );

    // The service's verdict, or nothing else to pick: a search that found
    // nothing it can open, or failed, still leaves the agent to ask.
    const askOffered =
        askMode ||
        (hasQuery &&
            view.kind === "results" &&
            (display.kind === "empty" || display.kind === "error"));

    // Browse always lists something; results once a routable row lands or the
    // Ask row is up, which is when ResultsView renders the listbox. A held list
    // is hidden from assistive tech, so it does not count.
    const listShown = view.kind === "browse" || display.kind === "rows" || askOffered;

    const routability = useMemo(
        () => summarizeRoutability(state.committedResults, isRoutable),
        [state.committedResults, isRoutable],
    );

    /**
     * The `Search` phase events (see {@link SearchPhase}), counted off the
     * committed ranking. Time-to-rows rides on the phase event.
     */
    const searchRef = useRef<ITrackedSearch | null>(null);

    /**
     * The assist taken just before this search started, claimed once. Every
     * tracker's accepted id is consumed, so none is left for a later search.
     */
    const trackersRef = useRef({ guessLearn, correctionLearn });
    useEffect(() => {
        trackersRef.current = { guessLearn, correctionLearn };
    }, [guessLearn, correctionLearn]);
    const takeLedBy = useCallback((): ITrackedSearch["ledBy"] => {
        const kind = pendingLedByRef.current;
        pendingLedByRef.current = null;
        const t = trackersRef.current;
        const taken = {
            did_you_mean: t.correctionLearn.takeAccepted(),
            inline_completion: t.guessLearn.takeAccepted(),
            query_suggestions: null,
        };
        const id = kind === null ? null : taken[kind];
        return kind === null || id === null ? null : { ledByAssist: kind, ledByAssistId: id };
    }, []);

    const sendSearchPhase = useCallback((search: ITrackedSearch, searchPhase: SearchPhase) => {
        learnRef.current.learnSearchPhase(
            { searchId: search.searchId, traceId: search.traceId },
            {
                searchPhase,
                query: search.query,
                rankSource: "committed",
                durationMs: search.startedAt != null ? Date.now() - search.startedAt : undefined,
                timeToRowsMs:
                    search.startedAt != null && search.rowsAt != null
                        ? search.rowsAt - search.startedAt
                        : undefined,
                ...(search.counts ?? NO_COUNTS),
                ...(search.ledBy ?? {}),
            },
        );
    }, []);

    /** Reports how the user left the search on screen. Refs only: the unmount calls it. */
    const leaveSearch = useCallback(() => {
        const search = searchRef.current;
        if (search === null || search.left) {
            return;
        }
        search.left = true;
        // A row opened, or the query handed to Ask, is the user moving on with
        // the search, however far its stream had got.
        if (search.used || search.failed) {
            return;
        }
        sendSearchPhase(search, search.completeSent ? "abandoned" : "interrupted");
    }, [sendSearchPhase]);

    useEffect(() => {
        if (!state.searchId) {
            return;
        }
        let search = searchRef.current;
        if (search === null || search.searchId !== state.searchId) {
            search = {
                searchId: state.searchId,
                traceId: null,
                query: state.searchQuery,
                startedAt: state.searchStartTime,
                counts: null,
                rowsAt: null,
                completeSent: false,
                failed: false,
                used: false,
                left: false,
                ledBy: takeLedBy(),
            };
            searchRef.current = search;
        }
        // The trace event can land at any point in the stream.
        search.traceId = state.traceId;
        if (search.left) {
            return;
        }
        // Counted the way the view shows it. Finishing without a ranking shows
        // a failure with Retry, so it counts as one rather than as nothing
        // found. A stream cut after its ranking still shows the rows, so it
        // counts as finished; only one with nothing on screen failed.
        const cutAfterRows = state.phase === "error" && routability.renderedCount > 0;
        const finished = state.phase === "done" || state.phase === "empty" || cutAfterRows;
        if (
            (state.phase === "error" && !cutAfterRows) ||
            (finished && state.committedResults === null)
        ) {
            search.failed = true;
            return;
        }
        if (state.committedResults !== null) {
            search.counts = { resultCount: state.committedResults.length, ...routability };
            if (search.rowsAt === null) {
                search.rowsAt = Date.now();
                if (routability.renderedCount > 0) {
                    sawResultsRef.current = true;
                }
            }
        }
        if (!search.completeSent && finished) {
            search.completeSent = true;
            searchCountRef.current += 1;
            sendSearchPhase(search, "complete");
        }
    }, [
        state.searchId,
        state.phase,
        state.searchQuery,
        state.traceId,
        state.committedResults,
        state.searchStartTime,
        routability,
        sendSearchPhase,
        takeLedBy,
    ]);

    /**
     * The mount is the episode: open on mount, close on unmount. A reload or tab
     * close skips unmount and sends no close; the open alone marks it abandoned.
     */
    useEffect(() => {
        const openedAt = openedAtRef.current;
        learnRef.current.learnDialogOpened({
            trigger,
            openRecordEntity,
            recentCount: getRecentItems(userId).length,
            promptCount: getTryAskingPrompts({ openRecordEntity }).length,
        });
        // Reads the session's counters when it runs, not their values at open.
        return () => {
            leaveSearch();
            learnRef.current.learnDialogClosed({
                outcome: outcomeRef.current,
                msOpen: Date.now() - openedAt,
                searchCount: searchCountRef.current,
                askCount: askCountRef.current,
                sawResults: sawResultsRef.current,
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount: the mount is the episode
    }, []);

    // A ref rather than autoFocus: that attribute is unreliable through a portal,
    // and jsx-a11y/no-autofocus flags it.
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    /** The key of the search last started, so an edit that normalizes to it is not re-sent. */
    const searchedKeyRef = useRef("");
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearPendingSearch = useCallback(() => {
        if (debounceRef.current !== null) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
    }, []);

    useEffect(() => clearPendingSearch, [clearPendingSearch]);

    // What is on screen, not `state.errorKind`: a search that finished without
    // a ranking shows as an error while the raw state carries none.
    const searchFailed = display.kind === "error";

    const searchNow = useCallback(
        (key: string) => {
            clearPendingSearch();
            searchedKeyRef.current = key;
            doSearch(key);
        },
        [clearPendingSearch, doSearch],
    );

    const handleQueryChange = useCallback(
        (next: string) => {
            setQuery(next);
            // The row under the highlight is not the row about to be there.
            setChosenKey(null);
            clearPendingSearch();

            const key = normalizeQuery(next);
            if (key.length === 0) {
                searchedKeyRef.current = "";
                leaveSearch();
                cancel();
                return;
            }
            // A trailing space, or typing back to the query already searched.
            // Unless that search failed: editing is one way to try again.
            if (key === searchedKeyRef.current && !searchFailed) {
                return;
            }
            debounceRef.current = setTimeout(() => searchNow(key), QUERY_DEBOUNCE_MS);
        },
        [cancel, clearPendingSearch, leaveSearch, searchNow, searchFailed],
    );

    const handleRetry = useCallback(() => {
        if (liveKey.length > 0) {
            searchNow(liveKey);
        }
    }, [liveKey, searchNow]);

    // The Retry button unmounts as the search restarts, which would drop
    // focus to the page and take the dialog's keyboard handling with it.
    const handleRetryClick = useCallback(() => {
        handleRetry();
        inputRef.current?.focus();
    }, [handleRetry]);

    const canRetry = searchFailed && isRetryableSearchError(display.errorKind);

    // A search that failed for want of a connection is retried once there is one.
    const failedOffline = display.kind === "error" && display.errorKind === "network";
    useEffect(() => {
        if (!failedOffline) {
            return;
        }
        window.addEventListener("online", handleRetry);
        return () => window.removeEventListener("online", handleRetry);
    }, [failedOffline, handleRetry]);

    const announcement = useMemo(
        () =>
            view.kind === "results"
                ? describeResults(display, liveKey, corrected, isRoutable)
                : { status: "", alert: "" },
        [view.kind, display, liveKey, corrected, isRoutable],
    );

    const applyCorrection = useCallback(
        (corrected: string) => {
            learn.learnCorrectionApplied({
                searchId: state.searchId || null,
                traceId: state.traceId,
            });
            correctionLearn.accept({ navMethod: "click" });
            pendingLedByRef.current = "did_you_mean";
            handleQueryChange(corrected);
            // The button unmounts once the corrected query has no correction
            // of its own, and would take focus out of the dialog with it.
            inputRef.current?.focus();
        },
        [learn, state.searchId, state.traceId, handleQueryChange, correctionLearn],
    );

    const { remember } = suggest;
    /** Hands a question to the full-page Ask. An empty one opens Ask blank. */
    const handOff = useCallback(
        (question: string) => {
            askCountRef.current += 1;
            if (searchRef.current !== null) {
                searchRef.current.used = true;
            }
            outcomeRef.current = "handed_off";
            if (question.length > 0) {
                remember(question);
            }
            onAskFullPage(question);
            onClose();
        },
        [remember, onAskFullPage, onClose],
    );

    // A dictated phrase is not typed, so it is never completed.
    const handleDictated = useCallback(
        (next: string) => {
            setGuessAllowed(false);
            handleQueryChange(next);
        },
        [handleQueryChange],
    );
    const handleComposerSubmit = useCallback(
        (question: string) => {
            if (question.length > 0) {
                handOff(question);
            }
        },
        [handOff],
    );
    // The Ask page's question box, mic and arrow; it asks with `query`, so a
    // guess still selected is not accepted.
    const composer = useAskComposerDictation({
        value: query,
        onChange: handleDictated,
        disabled: false,
        isBusy: false,
        onSubmit: handleComposerSubmit,
    });
    const submitAsQuestion = composer.submit;

    const shownQuery = composer.isRecording ? composer.shownValue : query + inlineSuffix;

    /** The one Ask row: exactly what is typed, never the guess. */
    const askQuestion = query.trim();
    const askRow = useMemo(
        (): ISearchDialogRow | null =>
            askOffered
                ? {
                      key: "ask",
                      kind: "ask",
                      icon: null,
                      // The agent's teal, as on the Ask page's steps.
                      leading: (
                          <span className="flex items-center gap-1">
                              {/* Blueprint icons set their own color, so the
                                  teal goes on the icon itself. */}
                              <span className="flex shrink-0" aria-hidden="true">
                                  <IconSparkle size="sm" className="text-teal-60" />
                              </span>
                              <BTText size="normal-md" tag="span" className="text-teal-60">
                                  Ask
                              </BTText>
                          </span>
                      ),
                      title: askQuestion,
                      subtitle: null,
                      amount: null,
                      status: null,
                      trailingLabel: null,
                      onSelect: () => submitAsQuestion(),
                  }
                : null,
        [askOffered, askQuestion, submitAsQuestion],
    );

    const handleResultOpened = useCallback(
        (
            hit: ISearchHit,
            title: string,
            subtitle: string | null,
            rank: number,
            navMethod: NavMethod,
        ) => {
            addRecentItem(userId, hit, { title, subtitle });
            outcomeRef.current = "record_opened";
            if (searchRef.current !== null) {
                searchRef.current.used = true;
            }
            learn.learnRecordOpened(
                { searchId: state.searchId || null, traceId: state.traceId },
                {
                    from: "result",
                    entity: hit.entity,
                    recordId: hit.id,
                    rank,
                    // The committed ranking is the only list the dialog renders,
                    // so a rank from anywhere else would be a different number.
                    rankSource: "committed",
                    navMethod,
                    timeToClickMs:
                        state.searchStartTime != null
                            ? Date.now() - state.searchStartTime
                            : undefined,
                    msFromOpen: Date.now() - openedAtRef.current,
                },
            );
            onClose();
        },
        [userId, onClose, learn, state.searchId, state.traceId, state.searchStartTime],
    );

    const handleRecentOpened = useCallback(
        (hit: ISearchHit, rank: number, navMethod: NavMethod) => {
            outcomeRef.current = "record_opened";
            learn.learnRecordOpened(
                { searchId: null, traceId: null },
                {
                    from: "recent",
                    entity: hit.entity,
                    recordId: hit.id,
                    rank,
                    // A recent's position is recency, not a ranking.
                    rankSource: null,
                    navMethod,
                    timeToClickMs: undefined,
                    msFromOpen: Date.now() - openedAtRef.current,
                },
            );
            onClose();
        },
        [learn, onClose],
    );

    const handleAskPrompt = useCallback(
        (prompt: string, promptIndex: number, promptCount: number) => {
            learn.learnPromptPicked({ promptIndex, promptCount });
            askCountRef.current += 1;
            outcomeRef.current = "handed_off";
            // "How is this job going?" means nothing on the Ask page unless the
            // job goes with it.
            if (tryAskingRefersToOpenRecord({ openRecordEntity })) {
                onAskFullPage(prompt, { aboutOpenRecord: true });
            } else {
                onAskFullPage(prompt);
            }
            onClose();
        },
        [onAskFullPage, onClose, learn, openRecordEntity],
    );

    const browseSections = useBrowseSections({
        userId,
        builderInfo,
        openRecordEntity,
        onAskPrompt: handleAskPrompt,
        onRecentOpened: handleRecentOpened,
    });
    const resultSections = useResultSections({
        display,
        query,
        builderInfo,
        onResultOpened: handleResultOpened,
    });

    /** The rows the keyboard can reach: not the held list, which is on screen but inert. */
    const navigableRows = useMemo(() => {
        if (view.kind === "browse") {
            return flattenRows(browseSections);
        }
        const resultRows = display.kind === "rows" ? flattenRows(resultSections) : [];
        // The Ask row leads, in every state, as ResultsView draws it.
        return askRow === null ? resultRows : [askRow, ...resultRows];
    }, [view.kind, display.kind, browseSections, resultSections, askRow]);

    /** The option id of a row by its place in the keyboard order. */
    const optionId = useCallback(
        (index: number) => `${SEARCH_DIALOG_OPTION_ID_PREFIX}${index}`,
        [],
    );

    // The Ask row when it is up, so Enter asks. Otherwise the first record: a
    // prompt row would make Enter fire a canned question.
    const defaultIndex =
        askRow !== null ? 0 : navigableRows.findIndex((row) => row.kind === "record");
    const chosenIndex =
        chosenKey === null ? -1 : navigableRows.findIndex((row) => row.key === chosenKey);
    const highlightedIndex = chosenIndex >= 0 ? chosenIndex : defaultIndex;
    const chooseRow = useCallback(
        (index: number) => setChosenKey(navigableRows[index]?.key ?? null),
        [navigableRows],
    );
    const highlightedKind = navigableRows[highlightedIndex]?.kind ?? null;
    const activeDescendant = highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined;
    const viewHighlight = highlightedIndex >= 0 ? highlightedIndex : NO_ROW;
    const enterAction = resolveEnterAction({ highlightedKind, hasQuery, canRetry });

    /**
     * The query an Enter was pressed on before its search had anything to
     * offer. The Ask row waits on the service's verdict, so a question typed
     * and sent at once would otherwise be lost. Null when no Enter is waiting.
     */
    const [queuedEnterKey, setQueuedEnterKey] = useState<string | null>(null);
    const verdictLanded = searchIsForQuery && state.searchType?.query === liveKey;
    // Held until the verdict and a row to act on, or the search's end: a
    // ranking that lands ahead of a nudge would otherwise open a record the
    // user meant to ask about.
    useEffect(() => {
        if (queuedEnterKey === null) {
            return;
        }
        // An edit is a new query; the Enter was for the old one.
        if (queuedEnterKey !== liveKey) {
            setQueuedEnterKey(null);
            return;
        }
        const row = navigableRows[highlightedIndex];
        if (!searchSettled && !(verdictLanded && row !== undefined)) {
            return;
        }
        setQueuedEnterKey(null);
        row?.onSelect?.("keyboard");
    }, [queuedEnterKey, liveKey, navigableRows, highlightedIndex, searchSettled, verdictLanded]);

    /**
     * cmd/ctrl+Enter is handled here and never registered as a global hotkey:
     * it is `HotkeyCommands.saveAndClose`. Hotkeys of whatever is open behind
     * the dialog (a record's Esc-to-close, its save-and-close) are kept quiet
     * by the dialog's own focus layer, not by `preventDefault` here, which
     * hotkeys-js ignores.
     */
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "Escape") {
                event.preventDefault();
                // The first Escape drops an inline guess; the next closes.
                if (inlineSuffix.length > 0) {
                    guessLearn.dismiss();
                    dropGuess();
                    return;
                }
                onClose();
                return;
            }

            if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                submitAsQuestion();
                return;
            }

            if (inlineSuffix.length > 0 && event.target === inputRef.current) {
                if (
                    (event.key === "Tab" && !event.shiftKey) ||
                    event.key === "ArrowRight" ||
                    event.key === "End"
                ) {
                    event.preventDefault();
                    const accepted = query + inlineSuffix;
                    const guessKey =
                        event.key === "Tab" ? "tab" : event.key === "End" ? "end" : "arrow";
                    learn.learnGuessAccepted(
                        { searchId: state.searchId || null, traceId: state.traceId },
                        {
                            key: guessKey,
                            guessFrom: prediction.from,
                            typedLength: query.length,
                            guessLength: inlineSuffix.length,
                        },
                    );
                    guessLearn.accept({ navMethod: guessKey });
                    pendingLedByRef.current = "inline_completion";
                    dropGuess();
                    handleQueryChange(accepted);
                    // The text on screen is unchanged, so the selection would
                    // stay: the caret goes to the end, as after typing it.
                    inputRef.current?.setSelectionRange(accepted.length, accepted.length);
                    return;
                }
                if (event.key === "ArrowLeft" || event.key === "Home") {
                    guessLearn.dismiss();
                    dropGuess();
                }
            }

            // With a modifier, because a bare Home/End in an editable combobox
            // belongs to the caret. Not with shift, which selects text.
            const jumpsToEdge =
                (event.ctrlKey || event.metaKey) &&
                !event.shiftKey &&
                (event.key === "Home" || event.key === "End");
            if (event.key === "ArrowDown" || event.key === "ArrowUp" || jumpsToEdge) {
                const rowCount = navigableRows.length;
                if (rowCount === 0) {
                    // The caret moves and the guess is no longer selected, so
                    // it is dropped rather than left to be typed onto.
                    if (inlineSuffix.length > 0) {
                        guessLearn.dismiss();
                        dropGuess();
                    }
                    return;
                }
                event.preventDefault();
                let next: number;
                if (jumpsToEdge) {
                    next = event.key === "Home" ? 0 : rowCount - 1;
                } else {
                    // Clamps rather than wraps; reappearing at the other end
                    // loses the user's place.
                    const step = event.key === "ArrowDown" ? 1 : -1;
                    next = Math.max(0, Math.min(highlightedIndex + step, rowCount - 1));
                }
                chooseRow(next);
                // Here rather than on every highlight change, so a hovered row
                // never scrolls the list out from under the pointer.
                document.getElementById(optionId(next))?.scrollIntoView({ block: "nearest" });
                // So the Enter that follows opens this row, not a focused button.
                if (event.target !== inputRef.current) {
                    inputRef.current?.focus();
                }
                return;
            }

            if (event.key === "Enter") {
                // A focused button owns its own Enter. Rows never hold focus,
                // so only the input's Enter is handled here.
                if (event.target !== inputRef.current) {
                    return;
                }
                // The field is a textarea, but a query is one line: Enter never
                // breaks it. Shift+Enter does, as in the Ask page's composer.
                if (event.shiftKey) {
                    return;
                }
                event.preventDefault();
                const onSelect = navigableRows[highlightedIndex]?.onSelect;
                if (onSelect) {
                    onSelect("keyboard");
                    return;
                }
                // Nothing highlighted, so Enter only means anything as another
                // try at a search that failed.
                if (canRetry) {
                    handleRetry();
                    return;
                }
                // The search for this query has not answered yet: Enter waits
                // for it, and the debounce is skipped so the wait is short.
                if (hasQuery && !searchSettled) {
                    if (debounceRef.current !== null) {
                        searchNow(liveKey);
                    }
                    setQueuedEnterKey(liveKey);
                }
                return;
            }

            if (event.key !== "Tab") {
                return;
            }

            const focusable = event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE);
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (!first || !last) {
                return;
            }
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        },
        [
            canRetry,
            handleRetry,
            hasQuery,
            searchSettled,
            searchNow,
            liveKey,
            highlightedIndex,
            navigableRows,
            onClose,
            submitAsQuestion,
            inlineSuffix,
            dropGuess,
            query,
            handleQueryChange,
            learn,
            prediction.from,
            state.searchId,
            state.traceId,
            optionId,
            chooseRow,
            guessLearn,
        ],
    );

    /**
     * A press on anything in the card that cannot take focus would drop focus
     * to <body>, outside the panel whose keydown carries Esc, the arrows and
     * Enter. It goes to the input instead, which is where typing belongs.
     *
     * Only while the dialog holds focus. With nothing focused, which on a
     * phone is a dismissed keyboard, focusing the input would summon it again.
     */
    const handleCardMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target instanceof Element && event.target.closest(FOCUSABLE)) {
            return;
        }
        if (!panelRef.current?.contains(document.activeElement)) {
            return;
        }
        event.preventDefault();
        inputRef.current?.focus();
    }, []);

    return createPortal(
        <FocusProvider>
            {/* The feedback form is a BTModal (z-index 100), so the dialog
                drops beneath it, and its mask, while the form is open. */}
            <div
                className={cn(
                    "fixed inset-0 bg-black opacity-30",
                    isFeedbackOpen ? "z-2" : "z-max",
                )}
                onClick={onClose}
                data-testid="search-dialog-scrim"
            />
            {/* Owns the keydown because it is the surface: there is no single
                control to hang the focus trap on, and giving the card a role
                would nest a second dialog inside this one.

                `pointer-events-none` because it spans the full width at the
                card's vertical band, where it would otherwise eat the clicks
                beside the card that belong to the scrim. */}
            <div
                ref={panelRef}
                className={cn(
                    "pointer-events-none fixed inset-0 flex justify-center sm:bottom-auto sm:top-16 sm:px-4",
                    isFeedbackOpen ? "z-2" : "z-max",
                )}
                onKeyDown={handleKeyDown}
                role="dialog"
                aria-modal="true"
                aria-label="Search or ask anything"
                data-testid="search-dialog"
            >
                <div
                    className="search-dialog-card pointer-events-auto flex h-full w-full flex-col overflow-hidden border-default bg-surface-default sm:h-auto sm:rounded-xl sm:border sm:shadow-overlay"
                    onMouseDown={handleCardMouseDown}
                    data-testid="search-dialog-card"
                >
                    {/* The dialog's edge frames the field, so it sits flush; a
                        rounded bottom and a soft shadow, not a rule, set it off
                        from the list, as in the prototype. The list below
                        tucks up under it so the curve has something to sit
                        over; `z-1` keeps the field, and its shadow, on top. On a phone the sheet fills the screen,
                        with no scrim to tap and no Esc key, so a back arrow
                        leads the field; above `sm` Esc and the scrim close it.
                        Not `hidden sm:flex`: webforms CSS has a global
                        `.hidden{display:none!important}`. */}
                    <div
                        className="relative z-1 flex shrink-0 items-start rounded-b-lg bg-surface-default text-fg-default shadow-raised"
                        data-testid="search-dialog-header"
                    >
                        <BTButton
                            type="tertiary"
                            icon={<IconArrowLeft />}
                            aria-label="Close search"
                            onClick={onClose}
                            className="ml-3 mt-4 flex shrink-0 sm:hidden"
                            data-testid="search-dialog-close"
                        />
                        {/* The Ask page's question box, mic and Ask arrow, with
                            the dialog's own keys. BTTextArea, not BTInput:
                            BTInput registers an empty hotkey whose cleanup
                            clears the shared hotkeys-js table, and this dialog
                            unmounts on every close. */}
                        <div className="flex min-w-0 flex-1 flex-col gap-3">
                            <AskComposerField
                                fieldRef={fieldRef}
                                composer={composer}
                                // No Ask arrow: the Ask row is how the dialog asks.
                                actions={
                                    <AskComposerMic
                                        composer={composer}
                                        disabled={false}
                                        testIdPrefix="search-dialog"
                                    />
                                }
                                testIdPrefix="search-dialog"
                                flush
                            >
                                <div
                                    ref={(wrapper) => {
                                        inputRef.current =
                                            wrapper?.querySelector("textarea") ?? null;
                                    }}
                                >
                                    <BTTextArea
                                        id="searchDialogQuery"
                                        data-testid="search-dialog-input"
                                        fieldRef={fieldRef}
                                        value={shownQuery}
                                        onChange={(_field, next) => {
                                            // A character typed may be completed; a deletion
                                            // never is (Backspace over the selected part
                                            // removes just that part).
                                            const inputType = lastInputTypeRef.current;
                                            lastInputTypeRef.current = null;
                                            setGuessAllowed(
                                                inputType === null ||
                                                    inputType.startsWith("insert"),
                                            );
                                            // A taken assist that started no search did not lead this one.
                                            takeLedBy();
                                            handleQueryChange(next);
                                        }}
                                        onInput={(e) => {
                                            const inputType = (e.nativeEvent as InputEvent)
                                                .inputType;
                                            lastInputTypeRef.current =
                                                typeof inputType === "string" ? inputType : null;
                                        }}
                                        // `onBlur` is BTTextArea's form blur, which it would
                                        // otherwise also fire on every change.
                                        blurOnChange={false}
                                        onBlur={() => undefined}
                                        // Searches on every keystroke and opens on Enter, so a
                                        // debounced value would run a keystroke behind.
                                        debouncingContext={{ useDebouncing: false }}
                                        minRows={1}
                                        maxRows={4}
                                        onMouseDown={() => {
                                            if (inlineSuffix.length > 0) {
                                                guessLearn.dismiss();
                                            }
                                            dropGuess();
                                        }}
                                        placeholder={
                                            composer.isRecording
                                                ? ASK_COMPOSER_LISTENING_PLACEHOLDER
                                                : "Search or ask"
                                        }
                                        aria-label="Search or ask a question"
                                        role="combobox"
                                        aria-expanded={listShown}
                                        aria-controls={SEARCH_DIALOG_LISTBOX_ID}
                                        aria-activedescendant={activeDescendant}
                                        aria-autocomplete="both"
                                        className={ASK_COMPOSER_TEXTAREA_CLASS}
                                    />
                                </div>
                            </AskComposerField>
                        </div>
                    </div>

                    {/* The scroller for every view, so its scrollbar sits on the
                        dialog's edge, outside the rows' padding. The arrow keys
                        scroll it by moving the highlight (scrollIntoView). */}
                    <div
                        className="-mt-5 flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-4 pt-5"
                        data-testid="search-dialog-content"
                        data-view={view.kind}
                    >
                        {view.kind === "browse" && (
                            <BrowseView
                                sections={browseSections}
                                highlightedIndex={viewHighlight}
                                onHoverRow={chooseRow}
                            />
                        )}
                        {view.kind === "results" && (
                            <ResultsView
                                display={display}
                                sections={resultSections}
                                query={query}
                                askRow={askRow}
                                highlightedIndex={viewHighlight}
                                onHoverRow={chooseRow}
                                foundSoFar={
                                    display.kind === "searching"
                                        ? state.results.filter((hit) => isRoutable(hit)).length
                                        : 0
                                }
                                corrected={corrected}
                                onApplyCorrection={applyCorrection}
                                onRetry={handleRetryClick}
                            />
                        )}
                    </div>

                    {/* Mounted for the dialog's lifetime and outside the view
                        switch: a live region that mounts along with its text
                        is often not announced. */}
                    <div
                        className="sr-only"
                        role="status"
                        aria-live="polite"
                        aria-atomic="true"
                        data-testid="search-dialog-status"
                    >
                        {announcement.status}
                    </div>
                    <div className="sr-only" role="alert" data-testid="search-dialog-alert">
                        {announcement.alert}
                    </div>

                    <SearchDialogFooter
                        enterAction={enterAction}
                        canAsk={hasQuery}
                        canAccept={inlineSuffix.length > 0}
                        feedback={
                            <BTButton
                                type="link"
                                onClick={() => setIsFeedbackOpen(true)}
                                data-testid="search-dialog-feedback"
                            >
                                Not finding it? Tell us
                            </BTButton>
                        }
                    />
                </div>
            </div>
            {/* Outside the panel, whose keydown would take the form's Esc and
                arrows: React carries a portal's events up its React tree. */}
            {isFeedbackOpen && (
                <FeedbackWidget
                    feedbackTarget={FeedbackTarget.AiSearch}
                    extraInfo={feedbackExtraInfo}
                    onClose={closeFeedback}
                    onSubmitSuccess={closeFeedback}
                />
            )}
        </FocusProvider>,
        document.body,
    );
}
